import { useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import type { User } from "@supabase/supabase-js";
import { 
  LogOut, ShieldAlert, Sun, Moon, X, 
  Home, Calendar, Tag, User as UserIcon, Settings, Headset, ChevronRight,
  ShieldCheck
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

import { useUserRoles } from "@/hooks/useUserRoles";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useTheme } from "next-themes";
import { useTranslation } from "react-i18next";

import maleAvatarImg from "@/assets/avatar-male.png";
import femaleAvatarImg from "@/assets/avatar-female.png";

import { tAuto } from "@/lib/tAuto";
type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  loading: boolean;
  getInitials: () => string;
  onSignOut: () => Promise<void> | void;
};

// Swipe detection threshold
const SWIPE_THRESHOLD = 60;

type NavItem = {
  to: string;
  icon: React.ElementType;
  label: string;
  badge?: string;
};

export default function MobileUserDrawer({ open, onOpenChange, user, loading, getInitials, onSignOut }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const close = () => onOpenChange(false);
  const { isHost, isGuest, isSuperAdmin } = useUserRoles();
  const { theme, setTheme } = useTheme();

  // Swipe to close
  const touchStartX = useRef<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    if (deltaX > SWIPE_THRESHOLD) close();
    touchStartX.current = null;
  };

  useEffect(() => {
    if (open && !loading && !user) { close(); navigate("/auth"); }
  }, [open, loading, user, navigate]);

  const { profile, needsVerification } = useUserProfile();

  const getAvatarUrl = () => {
    if (profile?.avatar_url) return profile.avatar_url;
    if (profile?.gender === "male") return maleAvatarImg;
    if (profile?.gender === "female") return femaleAvatarImg;
    return null;
  };

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Build nav based on roles
  const mainItems: NavItem[] = [
    { to: "/", icon: Home, label: t('nav.home') || "Início" },
  ];
  
  if (isGuest || isSuperAdmin) {
    mainItems.push({ to: "/reservas", icon: Calendar, label: t('nav.myBookings') || "Reservas" });
    mainItems.push({ to: "/ofertas/nova", icon: Tag, label: t('nav.makeOffer') || "Oferta" });
  }
  
  if (isHost || isSuperAdmin) {
    mainItems.push({ to: "/dashboard/calendario", icon: Calendar, label: "Calendário" });
    mainItems.push({ to: "/seguro", icon: ShieldCheck, label: "Meu Seguro" });
  }

  const accountItems: NavItem[] = [
    { to: "/perfil", icon: UserIcon, label: t('nav.profile') || "Perfil", badge: needsVerification ? "!" : undefined },
    { to: "/configuracoes", icon: Settings, label: t('nav.settings') || "Configurações" }
  ];

  const isDark = theme === "dark";

  const drawerContent = (
    <div className={cn("fixed inset-0 z-[9999]", open ? "pointer-events-auto" : "pointer-events-none")} role="dialog" aria-modal="true" aria-hidden={!open}>
      {/* Backdrop */}
      <div className={cn("absolute inset-0 bg-black/30 backdrop-blur-[2px] transition-opacity duration-300", open ? "opacity-100" : "opacity-0")} onClick={close} />

      {/* Panel */}
      <aside
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        className={cn(
        "absolute top-0 right-0 h-full w-[85vw] max-w-[360px]",
        "bg-white dark:bg-zinc-950",
        "flex flex-col rounded-l-3xl",
        "shadow-2xl",
        "transform transition-transform duration-300 ease-out",
        open ? "translate-x-0" : "translate-x-full"
      )}>

        {/* ── User Header ── */}
        <div className="flex-shrink-0 px-5 pt-[max(env(safe-area-inset-top),16px)] pb-3">
          {loading ? (
            <div className="h-16 rounded-xl bg-zinc-100 dark:bg-zinc-900 animate-pulse" />
          ) : user ? (
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="relative flex-shrink-0">
                  <Avatar className="h-12 w-12 shadow-sm border border-zinc-100 dark:border-zinc-800">
                    {getAvatarUrl() ? <AvatarImage src={getAvatarUrl()!} alt="Avatar" className="object-cover" /> : null}
                    <AvatarFallback className="bg-zinc-100 text-zinc-600 font-bold text-lg">{getInitials()}</AvatarFallback>
                  </Avatar>
                  {needsVerification && (
                    <div className="absolute -top-0.5 -right-0.5 flex h-4 w-4">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                      <span className="relative inline-flex rounded-full h-4 w-4 bg-primary items-center justify-center shadow-sm">
                        <ShieldAlert className="w-2.5 h-2.5 text-white" />
                      </span>
                    </div>
                  )}

                </div>
                <div className="flex flex-col justify-center">
                  <p className="font-bold text-zinc-900 dark:text-zinc-50 text-[15px] truncate leading-tight">
                    {user.user_metadata?.full_name || "Utilizador"}
                  </p>
                  <p className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate mb-1">{user.email}</p>
                  <span className="inline-flex items-center text-[10px] font-semibold text-[#E8513D] bg-[#E8513D]/10 px-2 py-0.5 rounded-full w-max">
                    {isSuperAdmin ? "Super Admin" : isHost ? "Proprietário" : "Hóspede"}
                  </span>
                </div>
              </div>
              
              {/* Close Button */}
              <button 
                onClick={close} 
                className="flex-shrink-0 w-8 h-8 rounded-full border border-zinc-200 dark:border-zinc-800 flex items-center justify-center text-zinc-500 hover:bg-zinc-50 dark:hover:bg-zinc-900 active:scale-95 transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : null}
        </div>

        <div className="mx-6 h-[1px] bg-gradient-to-r from-transparent via-[#DCD6CC]/80 dark:via-[#DCD6CC]/30 to-transparent" />

        {/* ── Navigation List ── */}
        <nav className="flex-1 px-4 py-2 overflow-y-auto flex flex-col gap-1.5 scrollbar-hide">
          {mainItems.map((item) => {
            const active = location.pathname === item.to;
            const Icon = item.icon;
            
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={close}
                className={cn(
                  "flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-200 group",
                  active 
                    ? "bg-[#E8513D]/10 dark:bg-[#E8513D]/15" 
                    : "hover:bg-zinc-50 dark:hover:bg-zinc-900/50"
                )}
              >
                <div className={cn(
                  "w-10 h-10 rounded-xl flex items-center justify-center transition-colors shadow-sm dark:shadow-none",
                  active 
                    ? "bg-[#E8513D]/10 text-[#E8513D] border border-[#E8513D]/20" 
                    : "bg-white dark:bg-[#1C1C1E] text-zinc-600 dark:text-zinc-400 border border-zinc-200/80 dark:border-[#2A2A2A] group-hover:border-zinc-300 dark:group-hover:border-zinc-700"
                )}>
                  <Icon className={cn("w-5 h-5", active && "text-[#E8513D]")} strokeWidth={2} />
                </div>
                
                <span className={cn(
                  "flex-1 text-[15px] font-semibold", 
                  active ? "text-[#E8513D]" : "text-zinc-800 dark:text-zinc-200 group-hover:text-zinc-900 dark:group-hover:text-white"
                )}>
                  {item.label}
                </span>
              </Link>
            );
          })}

          <div className="h-[1px] bg-gradient-to-r from-transparent via-[#DCD6CC]/80 dark:via-[#DCD6CC]/30 to-transparent my-1" />
          <p className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider px-3 mb-0.5">Conta</p>

          {accountItems.map((item) => {
            const active = location.pathname === item.to;
            const Icon = item.icon;
            
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={close}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-2xl transition-all duration-200 group",
                  active 
                    ? "bg-[#E8513D]/10 dark:bg-[#E8513D]/15" 
                    : "hover:bg-zinc-50 dark:hover:bg-zinc-900/50"
                )}
              >
                <div className={cn(
                  "w-8 h-8 rounded-[10px] flex items-center justify-center transition-colors shadow-sm dark:shadow-none",
                  active 
                    ? "bg-[#E8513D]/10 text-[#E8513D] border border-[#E8513D]/20" 
                    : "bg-white dark:bg-[#1C1C1E] text-zinc-600 dark:text-zinc-400 border border-zinc-200/80 dark:border-[#2A2A2A] group-hover:border-zinc-300 dark:group-hover:border-zinc-700"
                )}>
                  <Icon className={cn("w-4 h-4", active && "text-[#E8513D]")} strokeWidth={2} />
                </div>
                
                <span className={cn(
                  "flex-1 text-[14px] font-semibold", 
                  active ? "text-[#E8513D]" : "text-zinc-800 dark:text-zinc-200 group-hover:text-zinc-900 dark:group-hover:text-white"
                )}>
                  {item.label}
                </span>
                
                {item.badge && (
                  <span className="w-5 h-5 text-[10px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center shadow-[0_0_10px_rgba(239,68,68,0.5)]">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}

          {needsVerification && (
            <Link to="/perfil" onClick={close} className="mt-1 flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-500/10 dark:to-rose-500/5 border border-red-200/50 dark:border-red-500/20 backdrop-blur-sm transition-all hover:shadow-sm">
              <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center shrink-0">
                <ShieldAlert className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
              </div>
              <span className="text-[12px] text-red-700 dark:text-red-400 font-semibold tracking-tight">{tAuto("auto.components_layout_mobileuserdrawer.1", "Verificação pendente")}</span>
            </Link>
          )}
          {/* ── Footer / Support & Logout ── */}
          <div className="mt-8 space-y-2.5 pb-[max(env(safe-area-inset-bottom),16px)]">
            {/* Support Card */}
            <div className="bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-100 dark:border-zinc-800 rounded-[20px] p-2.5 flex items-center justify-between shadow-sm gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-9 h-9 shrink-0 rounded-full bg-[#E8513D]/10 flex items-center justify-center text-[#E8513D]">
                  <Headset className="w-[18px] h-[18px]" strokeWidth={2} />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="font-bold text-zinc-900 dark:text-zinc-50 text-[13px] truncate">Suporte</span>
                  <span className="text-[10px] text-zinc-500 dark:text-zinc-400 truncate">{tAuto("auto.components_layout_mobileuserdrawer.2", "Estamos aqui para ajudar")}</span>
                </div>
              </div>
              <button 
                onClick={() => { close(); window.dispatchEvent(new CustomEvent('open-bera-ia-widget')); }}
                className="flex shrink-0 items-center gap-1 px-3 py-1.5 rounded-full bg-white dark:bg-zinc-900 text-[#E8513D] text-[11px] font-bold shadow-sm hover:bg-zinc-50 transition-colors whitespace-nowrap"
              >
                Bera <ChevronRight className="w-3 h-3" />
              </button>
            </div>

            {/* Logout Button */}
            <button
              onClick={async () => { close(); await onSignOut(); }}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-[16px] border border-[#E8513D]/20 bg-[#E8513D]/5 text-[#E8513D] hover:bg-[#E8513D]/10 active:scale-[0.98] transition-all font-bold text-[13px]"
            >
              <LogOut className="w-4 h-4" />
              Terminar Sessão
            </button>
          </div>
        </nav>
      </aside>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(drawerContent, document.body);
}
